import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconLock, IconUser, IconLogout, IconWallet, IconReceipt, IconTrendingUp, IconCalendar, IconScissors, IconChartBar, IconCash, IconArrowDown, IconAlertCircle, IconCheck, IconClock, IconX, IconEdit, IconDeviceFloppy } from '@tabler/icons-react';
import { ThemeToggle } from '../components/ThemeToggle';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../lib/api';

const MasterPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [master, setMaster] = useState(null);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('balance');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  const loadPayments = useCallback(async () => {
    try {
      const response = await api.get('/master/payments');
      setPayments(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadBalance = useCallback(async () => {
    try {
      const response = await api.get('/master/balance');
      setBalance(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadPayouts = useCallback(async () => {
    try {
      const response = await api.get('/master/payouts');
      setPayouts(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPayments(), loadBalance(), loadPayouts()]);
    setLoading(false);
  }, [loadPayments, loadBalance, loadPayouts]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const masterData = localStorage.getItem('masterData');
    if (token && masterData) {
      setIsLoggedIn(true);
      setMaster(JSON.parse(masterData));
      loadAllData();
    }
  }, [loadAllData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/master/login', { login, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('masterData', JSON.stringify(response.data.master));
      setIsLoggedIn(true);
      setMaster(response.data.master);
      toast.success('Вход выполнен');
      loadAllData();
    } catch (error) {
      toast.error('Неверные учетные данные');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('masterData');
    setIsLoggedIn(false);
    setLogin('');
    setPassword('');
    setMaster(null);
    setPayments([]);
    setBalance(null);
    setPayouts([]);
  };

  const handleRequestPayout = async () => {
    const amountRub = parseFloat(payoutAmount);
    if (!amountRub || amountRub <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }
    const amountKop = Math.round(amountRub * 100);
    setPayoutLoading(true);
    try {
      await api.post('/master/request-payout', { amount: amountKop });
      toast.success(`Запрос на выплату ${amountRub}₽ отправлен на одобрение`);
      setShowPayoutModal(false);
      setPayoutAmount('');
      await Promise.all([loadBalance(), loadPayouts()]);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Ошибка при запросе выплаты';
      toast.error(detail);
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await api.put('/master/profile', profileForm);
      setMaster(prev => ({ ...prev, ...response.data }));
      localStorage.setItem('masterData', JSON.stringify({ ...master, ...response.data }));
      setEditingProfile(false);
      toast.success('Профиль обновлён');
    } catch (error) {
      toast.error('Ошибка при сохранении');
    }
  };

  const fmt = (amount) => amount != null ? (amount / 100).toLocaleString('ru-RU') : '0';
  const fmtDate = (ds) => ds ? new Date(ds).toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Samara' }) : '';

  const successfulPayments = payments.filter(p => p.status === 'succeeded');
  const totalEarnings = balance?.total_earned || successfulPayments.reduce((s, p) => s + (p.master_amount || p.masterAmount || 0), 0);

  // Charts
  const paymentsByDate = {};
  successfulPayments.forEach(p => {
    const date = new Date(p.created_at || p.createdAt).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Samara' });
    paymentsByDate[date] = (paymentsByDate[date] || 0) + (p.master_amount || p.masterAmount || 0);
  });
  const chartData = Object.entries(paymentsByDate).slice(-14).map(([name, v]) => ({ name, earnings: v / 100 }));

  const statusBadge = (status) => {
    const map = {
      succeeded: { label: 'Выплачено', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
      processing: { label: 'В обработке', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      pending: { label: 'Ожидание', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
      pending_approval: { label: 'Ожидает одобрения', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
      rejected: { label: 'Отклонено', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      failed: { label: 'Ошибка', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
      waiting: { label: 'Ожидание', cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
    };
    const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>;
  };

  // LOGIN FORM
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background bg-noise flex items-center justify-center p-4">
        <Card className="max-w-sm w-full bg-card/70 backdrop-blur-sm border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[24px] p-6">
          <div className="mb-6 text-center">
            <h2 className="font-['Playfair_Display'] text-2xl mb-2">Вход для мастеров</h2>
            <p className="text-sm text-[color:hsl(var(--foreground))]/70">Введите логин и пароль</p>
          </div>
          <form onSubmit={handleLogin} data-testid="master-login-form">
            <div className="mb-4">
              <label className="block text-sm mb-1" htmlFor="login">Логин</label>
              <div className="relative">
                <IconUser size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:hsl(var(--foreground))]/50" />
                <input id="login" type="text" value={login} onChange={(e) => setLogin(e.target.value)} className="w-full h-10 pl-9 pr-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" required data-testid="master-login-input" placeholder="anna" />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm mb-1" htmlFor="password">Пароль</label>
              <div className="relative">
                <IconLock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:hsl(var(--foreground))]/50" />
                <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-10 pl-9 pr-3 border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))]" required data-testid="master-password-input" placeholder="anna123" />
              </div>
            </div>
            <button type="submit" className="w-full inline-flex items-center justify-center h-11 px-6 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white transition-[background-color] duration-200 ease-out hover:bg-[color:hsl(var(--brand-orange-700))]" data-testid="master-login-submit">Войти</button>
          </form>
          <button onClick={() => navigate('/')} className="w-full mt-4 text-sm text-[color:hsl(var(--foreground))]/70 hover:text-[color:hsl(var(--foreground))] transition-colors">
            &larr; На главную
          </button>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'balance', label: 'Баланс', icon: IconWallet },
    { id: 'works', label: 'Работы', icon: IconScissors },
    { id: 'payouts', label: 'Выплаты', icon: IconCash },
    { id: 'profile', label: 'Профиль', icon: IconUser },
  ];

  return (
    <div className="min-h-screen bg-background bg-noise">
      {/* Header */}
      <header className="border-b-[0.5px] border-[color:hsl(var(--hairline))] bg-card/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div>
            <h1 className="font-['Playfair_Display'] text-xl font-semibold">Личный кабинет</h1>
            <p className="text-xs text-[color:hsl(var(--foreground))]/70">{master?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={handleLogout} className="inline-flex items-center gap-2 px-4 h-9 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] hover:border-[color:hsl(var(--brand-orange))] transition-[border-color] duration-200" data-testid="master-logout-button">
              <IconLogout size={16} />
              <span className="text-sm hidden sm:inline">Выйти</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b-[0.5px] border-[color:hsl(var(--hairline))] bg-card/40">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-[color:hsl(var(--brand-orange))] text-[color:hsl(var(--brand-orange))]' : 'border-transparent text-[color:hsl(var(--foreground))]/60 hover:text-[color:hsl(var(--foreground))]'}`}>
              <tab.icon size={16} />{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? <Skeleton className="h-96" /> : (
          <>
            {/* ============ БАЛАНС ============ */}
            {activeTab === 'balance' && (
              <div className="space-y-6">
                {/* Main Balance */}
                <Card className="bg-gradient-to-br from-[color:hsl(var(--brand-orange))] to-[color:hsl(var(--brand-orange-700))] text-white rounded-[24px] p-6 sm:p-8" data-testid="master-balance-card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <IconWallet size={20} />
                        <span className="text-sm opacity-90">Доступно к выводу</span>
                      </div>
                      <div className="text-4xl sm:text-5xl font-bold tabular-nums" data-testid="master-available-balance">
                        {fmt(balance?.available_balance || 0)} ₽
                      </div>
                    </div>
                    <button onClick={() => setShowPayoutModal(true)} data-testid="request-payout-btn"
                      className="flex items-center gap-2 px-5 py-3 rounded-[14px] bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-colors text-white font-medium">
                      <IconArrowDown size={18} />
                      <span className="hidden sm:inline">Вывести</span>
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/20">
                    <div>
                      <p className="text-xs opacity-70">Заработано</p>
                      <p className="text-lg font-semibold tabular-nums">{fmt(balance?.total_earned || 0)} ₽</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-70">Выплачено</p>
                      <p className="text-lg font-semibold tabular-nums">{fmt(balance?.total_paid_out || 0)} ₽</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-70">В обработке</p>
                      <p className="text-lg font-semibold tabular-nums">{fmt(balance?.pending_payouts || 0)} ₽</p>
                    </div>
                  </div>
                </Card>

                {/* Limits Info */}
                {balance?.limits && (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6" data-testid="limits-card">
                    <h3 className="font-medium text-sm mb-4 flex items-center gap-2 text-[color:hsl(var(--foreground))]/70">
                      <IconAlertCircle size={16} /> Лимиты выплат
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-[color:hsl(var(--foreground))]/50">Мин. выплата</p>
                        <p className="font-medium">{fmt(balance.limits.min_payout)} ₽</p>
                      </div>
                      <div>
                        <p className="text-xs text-[color:hsl(var(--foreground))]/50">Макс. выплата</p>
                        <p className="font-medium">{fmt(balance.limits.max_payout)} ₽</p>
                      </div>
                      <div>
                        <p className="text-xs text-[color:hsl(var(--foreground))]/50">Сегодня</p>
                        <p className="font-medium">{balance.limits.daily_payouts_used} / {balance.limits.max_daily_count}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[color:hsl(var(--foreground))]/50">В месяц</p>
                        <p className="font-medium">{balance.limits.monthly_payouts_used} / {balance.limits.max_monthly_count}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <IconScissors size={18} className="text-[color:hsl(var(--brand-orange))]" />
                      <span className="text-sm text-[color:hsl(var(--foreground))]/60">Всего услуг</span>
                    </div>
                    <div className="text-3xl font-bold tabular-nums">{successfulPayments.length}</div>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <IconTrendingUp size={18} className="text-[color:hsl(var(--brand-orange))]" />
                      <span className="text-sm text-[color:hsl(var(--foreground))]/60">Средний чек</span>
                    </div>
                    <div className="text-3xl font-bold tabular-nums">{fmt(successfulPayments.length > 0 ? totalEarnings / successfulPayments.length : 0)} ₽</div>
                  </Card>
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <IconCalendar size={18} className="text-[color:hsl(var(--brand-orange))]" />
                      <span className="text-sm text-[color:hsl(var(--foreground))]/60">Процент мастера</span>
                    </div>
                    <div className="text-3xl font-bold tabular-nums">{master?.masterPercent || 60}%</div>
                  </Card>
                </div>

                {/* Earnings Chart */}
                {chartData.length > 0 && (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6">
                    <h3 className="font-medium text-lg mb-4 flex items-center gap-2">
                      <IconChartBar size={20} className="text-[color:hsl(var(--brand-orange))]" />
                      Динамика заработка
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => `${v.toFixed(0)} ₽`} />
                        <Line type="monotone" dataKey="earnings" stroke="hsl(var(--brand-orange))" strokeWidth={2} dot={{ fill: 'hsl(var(--brand-orange))', r: 4 }} name="Заработок" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Card>
                )}
              </div>
            )}

            {/* ============ РАБОТЫ ============ */}
            {activeTab === 'works' && (
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl mb-6 flex items-center gap-2">
                  <IconScissors size={24} className="text-[color:hsl(var(--brand-orange))]" />
                  Проделанные работы
                </h2>
                {payments.length === 0 ? (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center">
                    <p className="text-[color:hsl(var(--foreground))]/70">Работ пока нет</p>
                  </Card>
                ) : (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-separate border-spacing-0" data-testid="master-works-table">
                        <thead>
                          <tr>
                            {['Дата', 'Общая сумма', 'Ваш заработок', 'Процент', 'Статус'].map(h => (
                              <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-4 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map(p => {
                            const total = p.total_amount || p.totalAmount || 0;
                            const masterAmt = p.master_amount || p.masterAmount || 0;
                            const pct = total > 0 ? ((masterAmt / total) * 100).toFixed(0) : 0;
                            return (
                              <tr key={p.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors">
                                <td className="px-4 py-3 text-[12px] tabular-nums text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{fmtDate(p.created_at || p.createdAt)}</td>
                                <td className="px-4 py-3 text-[13px] tabular-nums whitespace-nowrap">{fmt(total)} ₽</td>
                                <td className="px-4 py-3 text-[13px] tabular-nums font-medium text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{fmt(masterAmt)} ₽</td>
                                <td className="px-4 py-3"><span className="inline-block px-2 py-0.5 rounded-full text-xs bg-[color:hsl(var(--brand-orange))]/10 text-[color:hsl(var(--brand-orange))]">{pct}%</span></td>
                                <td className="px-4 py-3">{statusBadge(p.status)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* ============ ВЫПЛАТЫ ============ */}
            {activeTab === 'payouts' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-['Playfair_Display'] text-2xl flex items-center gap-2">
                    <IconCash size={24} className="text-[color:hsl(var(--brand-orange))]" />
                    История выплат
                  </h2>
                  <button onClick={() => setShowPayoutModal(true)} data-testid="request-payout-btn-tab"
                    className="flex items-center gap-2 px-4 h-10 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors text-sm font-medium">
                    <IconArrowDown size={16} /> Запросить выплату
                  </button>
                </div>
                {payouts.length === 0 ? (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-12 text-center">
                    <p className="text-[color:hsl(var(--foreground))]/70">Выплат пока нет</p>
                  </Card>
                ) : (
                  <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full border-separate border-spacing-0" data-testid="master-payouts-table">
                        <thead>
                          <tr>
                            {['Дата', 'Сумма', 'Статус', 'Получатель', 'ID'].map(h => (
                              <th key={h} className="text-left text-[12px] font-medium text-[color:hsl(var(--foreground))]/70 px-4 py-3 border-b-[0.5px] border-[color:hsl(var(--hairline))] whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.map(p => (
                            <tr key={p.id} className="hover:bg-[color:hsl(var(--muted))]/40 transition-colors" data-testid="payout-row">
                              <td className="px-4 py-3 text-[12px] tabular-nums text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{fmtDate(p.created_at)}</td>
                              <td className="px-4 py-3 text-[13px] tabular-nums font-medium whitespace-nowrap">{fmt(p.amount)} ₽</td>
                              <td className="px-4 py-3">{statusBadge(p.status)}</td>
                              <td className="px-4 py-3 text-[12px] text-[color:hsl(var(--foreground))]/70 whitespace-nowrap">{p.recipient_phone || p.recipient_card || '—'}</td>
                              <td className="px-4 py-3 text-[11px] font-mono text-[color:hsl(var(--foreground))]/50 max-w-[120px] truncate">{p.payout_reference}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* ============ ПРОФИЛЬ ============ */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="font-['Playfair_Display'] text-2xl flex items-center gap-2">
                    <IconUser size={24} className="text-[color:hsl(var(--brand-orange))]" />
                    Профиль и реквизиты
                  </h2>
                  {!editingProfile ? (
                    <button onClick={() => { setEditingProfile(true); setProfileForm({ inn: master?.inn || '', phone: master?.phone || '', cardNumber: master?.cardNumber || '' }); }}
                      className="flex items-center gap-2 px-4 h-9 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] hover:border-[color:hsl(var(--brand-orange))] transition-colors text-sm" data-testid="edit-profile-btn">
                      <IconEdit size={16} /> Редактировать
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={handleSaveProfile} className="flex items-center gap-2 px-4 h-9 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white text-sm" data-testid="save-profile-btn">
                        <IconDeviceFloppy size={16} /> Сохранить
                      </button>
                      <button onClick={() => setEditingProfile(false)} className="flex items-center gap-2 px-4 h-9 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm">
                        <IconX size={16} /> Отмена
                      </button>
                    </div>
                  )}
                </div>

                <Card className="bg-card border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[20px] p-6" data-testid="master-profile-card">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-medium text-sm text-[color:hsl(var(--foreground))]/70 uppercase tracking-wider">Личные данные</h3>
                      <ProfileField label="ФИО" value={master?.name} />
                      <ProfileField label="Логин" value={master?.login} />
                      <ProfileField label="ИНН" value={editingProfile ? profileForm.inn : master?.inn} editing={editingProfile} onChange={v => setProfileForm(f => ({ ...f, inn: v }))} placeholder="000000000000" testId="input-inn" />
                      <ProfileField label="Телефон" value={editingProfile ? profileForm.phone : master?.phone} editing={editingProfile} onChange={v => setProfileForm(f => ({ ...f, phone: v }))} placeholder="+7 (000) 000-00-00" testId="input-phone" />
                    </div>
                    <div className="space-y-4">
                      <h3 className="font-medium text-sm text-[color:hsl(var(--foreground))]/70 uppercase tracking-wider">Банковские реквизиты</h3>
                      <ProfileField label="Номер карты" value={editingProfile ? profileForm.cardNumber : master?.cardNumber} editing={editingProfile} onChange={v => setProfileForm(f => ({ ...f, cardNumber: v }))} placeholder="2200 0000 0000 0000" testId="input-card" mono important />
                    </div>
                  </div>
                </Card>

                {!master?.cardNumber && (
                  <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-[0.5px] border-yellow-200 dark:border-yellow-800 rounded-[20px] p-6" data-testid="no-requisites-warning">
                    <div className="flex items-start gap-3">
                      <IconAlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">Заполните реквизиты для получения выплат</p>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">Укажите номер банковской карты в профиле, чтобы получать выплаты.</p>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="payout-modal">
          <Card className="max-w-md w-full bg-card rounded-[24px] p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-['Playfair_Display'] text-xl">Запросить выплату</h3>
              <button onClick={() => setShowPayoutModal(false)} className="p-1 rounded-lg hover:bg-[color:hsl(var(--muted))]"><IconX size={20} /></button>
            </div>

            <div className="mb-4 p-3 bg-[color:hsl(var(--muted))] rounded-[12px]">
              <p className="text-xs text-[color:hsl(var(--foreground))]/60">Доступно к выводу</p>
              <p className="text-2xl font-bold tabular-nums" data-testid="modal-available">{fmt(balance?.available_balance || 0)} ₽</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Сумма выплаты (₽)</label>
              <input type="number" value={payoutAmount} onChange={e => setPayoutAmount(e.target.value)}
                min={balance?.limits?.min_payout ? balance.limits.min_payout / 100 : 100}
                max={Math.min(balance?.limits?.max_payout ? balance.limits.max_payout / 100 : 15000, (balance?.available_balance || 0) / 100)}
                className="w-full h-12 px-4 text-lg border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[12px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))] tabular-nums"
                placeholder="500" data-testid="payout-amount-input" />
            </div>

            {balance?.limits && (
              <div className="mb-4 text-xs text-[color:hsl(var(--foreground))]/50 space-y-1">
                <p>Мин: {fmt(balance.limits.min_payout)} ₽ | Макс: {fmt(balance.limits.max_payout)} ₽</p>
                <p>Получатель: {master?.cardNumber ? `Карта ${master.cardNumber}` : 'Не указан — укажите номер карты в профиле'}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowPayoutModal(false)} className="flex-1 h-11 rounded-[12px] border-[0.5px] border-[color:hsl(var(--hairline))] text-sm font-medium">Отмена</button>
              <button onClick={handleRequestPayout} disabled={payoutLoading || !payoutAmount} data-testid="confirm-payout-btn"
                className="flex-1 h-11 rounded-[12px] bg-[color:hsl(var(--brand-orange))] text-white text-sm font-medium disabled:opacity-50 hover:bg-[color:hsl(var(--brand-orange-700))] transition-colors flex items-center justify-center gap-2">
                {payoutLoading ? <IconClock size={16} className="animate-spin" /> : <IconCheck size={16} />}
                {payoutLoading ? 'Отправка...' : 'Подтвердить'}
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const ProfileField = ({ label, value, editing, onChange, placeholder, mono, important, testId }) => (
  <div>
    <p className={`text-xs mb-1 ${important ? 'text-[color:hsl(var(--brand-orange))] font-medium' : 'text-[color:hsl(var(--foreground))]/60'}`}>{label}{important && ' *'}</p>
    {editing && onChange ? (
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className={`w-full h-9 px-3 text-sm border-[0.5px] border-[color:hsl(var(--hairline))] rounded-[10px] bg-card focus:outline-none focus:ring-2 focus:ring-[color:hsl(var(--brand-orange))] ${mono ? 'font-mono' : ''}`}
        data-testid={testId} />
    ) : (
      <p className={`text-sm font-medium ${mono ? 'font-mono' : ''} ${!value ? 'text-[color:hsl(var(--foreground))]/30 italic' : ''}`}>
        {value || 'Не указано'}
      </p>
    )}
  </div>
);

export default MasterPage;
